import { useTranslation } from 'react-i18next';
import { ColorNordicGrey4 } from 'autogen/design-tokens/tokens';
import type { ProductPageViewModel } from 'autogen/swagger/all';
import { AdvisorRecommendsTitle } from 'autogen/translation-keys/trans-website-productregime';
import { CustomerReviews } from 'autogen/translation-keys/trans-website-productreview-list';
import { ConnectedContentPDPHeading } from 'autogen/translation-keys/trans-website-recommendation';
import AdvisorToolbox from 'components/advisorToolbox/toolbox';
import ProductDisplayConnectedContent from 'components/connectedContent/productDisplayConnectedContent/productDisplayConnectedContent';
import Layout from 'components/Layout';
import PrelaunchBanner from 'components/PrelaunchBanner/PrelaunchBanner';
import ProductRegime from 'components/product/regime/ProductRegime';
import Reviews from 'components/reviews/reviews';
import WhenSplitTestVariant from 'helpers/WhenSplitTestVariant';
import Flowbox from './components/Flowbox';
import ProductInformation from './components/ProductInformation';
import StyledProductRecommendationLoader from './shared/styles';

export default function ProductPage({ model }: { model: ProductPageViewModel }) {
    const { t } = useTranslation();
    const {
        productInformation,
        advisorToolbox,
        productRegime,
        connectedContentBlocks,
        recommendationRequest,
        reviews,
        emaLogo,
        prelaunchBanner,
        flowBoxModel,
        canSmartBundleBeRequested,
        chips
    } = model;

    const { productId, uaTrackingCategory } = productInformation;

    return (
        <Layout.Page>
            {/* Product Prelaunch Banner */}
            {prelaunchBanner && (
                <Layout.Section>
                    <PrelaunchBanner model={prelaunchBanner} />
                </Layout.Section>
            )}

            {/* Product Information */}
            <Layout.Section>
                <ProductInformation
                    productInformation={productInformation}
                    emaLogo={emaLogo}
                    canSmartBundleBeRequested={canSmartBundleBeRequested}
                    chips={chips}
                />
            </Layout.Section>

            {/* Advisor Toolbox */}
            {advisorToolbox && (
                <Layout.Section heading={advisorToolbox?.headline}>
                    <AdvisorToolbox
                        model={advisorToolbox}
                        inline
                        productId={productId}
                        trackingLabel={uaTrackingCategory}
                    />
                </Layout.Section>
            )}

            {/* Product Regime */}
            {productRegime && (
                <WhenSplitTestVariant variant="Control" systemSplitTestKey="PdpRegimesPlacement">
                    <Layout.Section heading={t(AdvisorRecommendsTitle)}>
                        <ProductRegime model={productRegime} />
                    </Layout.Section>
                </WhenSplitTestVariant>
            )}

            {/* Flowbox - User Generated Content */}
            {flowBoxModel && (
                <Layout.Section>
                    <Flowbox model={flowBoxModel} productId={productId} />
                </Layout.Section>
            )}

            {/* Product ConnectedContent */}
            {!!connectedContentBlocks.length && (
                <Layout.Section heading={t(ConnectedContentPDPHeading)}>
                    <ProductDisplayConnectedContent model={connectedContentBlocks} />
                </Layout.Section>
            )}

            {/* Product Recommendation */}
            {recommendationRequest && (
                <Layout.Section>
                    <StyledProductRecommendationLoader model={recommendationRequest?.recommendationRequest} />
                </Layout.Section>
            )}

            {/* Product Reviews */}
            {reviews.reviews && (
                <Layout.Section.WithFullWidthBackground backgroundColor={ColorNordicGrey4} heading={t(CustomerReviews)}>
                    <Reviews model={reviews.reviews} />
                </Layout.Section.WithFullWidthBackground>
            )}
        </Layout.Page>
    );
}
